import { Injectable, signal } from '@angular/core';
import { ApiService } from './api.service';
import { catchError, filter, fromEvent, interval, map, Observable, of, Subscription, tap } from 'rxjs';
import { Software } from '../../models/software.model';

const POLL_MS = 5 * 60 * 1000;

@Injectable({
    providedIn: 'root'
})
export class SoftwareService extends ApiService {

    private _software = signal<Software | null>(null);
    readonly software = this._software.asReadonly();

    private _updateAvailable = signal<Software | null>(null);
    readonly updateAvailable = this._updateAvailable.asReadonly();

    private _runningVersion = signal<string | null>(null);
    readonly runningVersion = this._runningVersion.asReadonly();
    private watching = false;
    private pollSub?: Subscription;

    init(): Observable<Software> {
        return this.fetch().pipe(
            tap(software => {
                this._software.set(software);
                this._runningVersion.set(software?.version ?? null);
                this.startWatching();
            }),
        );
    }

    dismissUpdate(): void {
        this._updateAvailable.set(null);
    }

    reload(): void {
        window.location.reload();
    }

    private fetch(): Observable<Software> {
        return super.get('/software').pipe(map(({ data }) => data));
    }

    private startWatching(): void {
        if (this.watching || typeof window === 'undefined') {
            return;
        }

        this.watching = true;
        this.pollSub = interval(POLL_MS).subscribe(() => this.checkForUpdate());
        fromEvent(document, 'visibilitychange')
            .pipe(filter(() => document.visibilityState === 'visible'))
            .subscribe(() => this.checkForUpdate());
    }

    private checkForUpdate(): void {
        this.fetch().pipe(catchError(() => of(null))).subscribe((software) => {
            if (!software) {
                return;
            }

            this._software.set(software);

            if (this.shouldPrompt(software.version)) {
                this._updateAvailable.set(software);
            }
        });
    }

    private shouldPrompt(version: string | null | undefined): boolean {
        return !!version && !!this._runningVersion() && version !== this._runningVersion();
    }
}
